import { ReactNode } from 'react';
import { css } from '@emotion/react';
import { FirstWordBoldText } from '@frontend/grid-dashboard';
import { Avatar, Button, PopoverMenu, Text, Truncated, usePopoverMenu } from '@frontend/design-system';
import { ScheduleRequestsTrackingIds } from '../../trackingIds';

export type ScheduleListItemVariant = 'widget' | 'overlappingPanel' | 'default';

type Props = {
  name: string;
  locationName: string;
  primaryMeta?: ReactNode;
  secondaryMeta?: ReactNode;
  menuItems: ReactNode;
  locationChip?: (locationName: string) => React.ReactNode;
  getTriggerProps: ReturnType<typeof usePopoverMenu>['getTriggerProps'];
  getMenuProps: ReturnType<typeof usePopoverMenu>['getMenuProps'];
  variant?: ScheduleListItemVariant;
  moreInfo?: ReactNode;
  onClick?: () => void;
};

export const ScheduleRequestItemHeaderUI = ({
  name,
  primaryMeta,
  secondaryMeta,
  locationName,
  menuItems,
  locationChip,
  getTriggerProps,
  getMenuProps,
  variant,
  moreInfo,
  onClick,
}: Props) => {
  const menuButtonProps = {
    getTriggerProps,
    getMenuProps,
    menuItems,
    locationChip,
    locationName,
  };
  return (
    <>
      <div css={containerStyles} onClick={onClick}>
        <div css={requestInfoStyle}>
          <Avatar name={name} size={variant === 'overlappingPanel' ? 'small' : 'large'} css={avatarStyle(variant)} />
          <div css={requestInfoTextStyle(variant)}>
            <div css={primaryInfoStyles(variant)}>
              {variant === 'overlappingPanel' ? (
                <Truncated as={FirstWordBoldText} css={nameStyle(variant)} size='medium'>
                  {name}
                </Truncated>
              ) : (
                <Truncated as={Text} css={nameStyle(variant)} weight='bold'>
                  {name}
                </Truncated>
              )}
              {primaryMeta}

              {variant === 'overlappingPanel' && <MenuButton {...menuButtonProps} />}
            </div>
            <div css={secondaryInfoStyles}>{secondaryMeta}</div>
            {moreInfo}
          </div>
        </div>

        {variant !== 'overlappingPanel' && <MenuButton {...menuButtonProps} />}
      </div>
    </>
  );
};

const MenuButton = ({
  getTriggerProps,
  getMenuProps,
  menuItems,
  locationChip,
  locationName,
}: {
  getTriggerProps: ReturnType<typeof usePopoverMenu>['getTriggerProps'];
  getMenuProps: ReturnType<typeof usePopoverMenu>['getMenuProps'];
  menuItems: ReactNode;
  locationChip?: (location: string) => React.ReactNode;
  locationName: string;
}) => (
  <div css={actionsStyles}>
    {locationChip && typeof locationChip === 'function' && locationChip(locationName)}
    <Button
      variant='secondary'
      iconName='more-small'
      trackingId={ScheduleRequestsTrackingIds.requestListItemMoreBtn}
      {...getTriggerProps()}
    />
    <PopoverMenu {...getMenuProps()}>{menuItems}</PopoverMenu>
  </div>
);

const containerStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  gap: var(--fabric-space-gap-md);
`;

const requestInfoStyle = css`
  display: flex;
  align-items: center;
  flex: 1;
  gap: var(--fabric-space-gap-sm);
`;

const requestInfoTextStyle = (variant: ScheduleListItemVariant = 'default') => css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  ${variant === 'overlappingPanel' && 'flex: 1;'}
`;

const primaryInfoStyles = (variant: ScheduleListItemVariant = 'default') => css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
  ${variant === 'overlappingPanel' && 'flex: 1; width: 100%;'}
  ${variant === 'default' && 'max-width: 260px;'}
  ${variant === 'widget' && 'flex-wrap: wrap;'}
`;

const secondaryInfoStyles = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const actionsStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-xs);
  margin-left: auto;
`;

const nameStyle = (variant: ScheduleListItemVariant = 'default') => css`
  ${variant === 'widget' && 'max-width: 220px; width: auto;'}
`;
const avatarStyle = (variant: ScheduleListItemVariant = 'default') => css`
  ${variant === 'overlappingPanel' &&
  `align-self: flex-start; 
    margin-top: var(--fabric-space-gap-xxs);`}
`;
